// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useRecordContext } from 'react-admin';
import { Card, CardContent, Typography, Chip, Table, TableBody, TableCell, TableHead, TableRow, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { getUserPartnerContext, IUserPartnerContext } from '../../Services/partners';

// Contexte partenaire d'un compte Quanti : ses appartenances et sa demande d'accès.
//
// Répond aux deux questions qu'on se pose devant une fiche utilisateur : « est-il
// rattaché à un partenaire ? » et « a-t-il demandé à le devenir ? ». Sans elles, un
// compte qui vient de l'espace partenaire paraît vide sans qu'on sache pourquoi.
//
// ⚠️ Tout se résout par EMAIL, pas par identifiant. Une appartenance posée par
// invitation porte `userId = 0` tant qu'elle n'est pas acceptée : c'est exactement ce
// cas-là qu'on veut voir, puisqu'il explique un compte « sans rien » alors qu'un accès
// l'attend.

// #region memberChip
// Une ligne de `qm_partner_members` dont `userId` vaut 0 est une INVITATION envoyée par
// email, pas une appartenance active. Le guard et `/me/partners` ne la reconnaissent pas
// tant qu'elle n'est pas acceptée — l'afficher comme un accès serait un contresens.
const memberChip = (userId: number) =>
	userId > 0 ? (
		<Chip size="small" color="success" label="Actif" />
	) : (
		<Chip size="small" label="Invitation en attente" />
	);
// #endregion

// #region UserPartnerCard
export const UserPartnerCard = () => {
	const record = useRecordContext();
	const email = record?.email;
	const [data, setData] = useState<IUserPartnerContext | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!email) return;
		setLoading(true);
		getUserPartnerContext(email)
			// Best-effort : api-partners peut être indisponible sans que la fiche
			// utilisateur, qui vient d'api-core, cesse d'être consultable.
			.then(setData)
			.catch(() => setData(null))
			.finally(() => setLoading(false));
	}, [email]);

	if (!email) return null;

	const memberships = data?.memberships || [];
	const signup = data?.signupRequest || null;

	return (
		<>
			<Card style={{ marginTop: 16 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Associated Partner
					</Typography>
					{loading && <CircularProgress size={20} />}
					{!loading && memberships.length === 0 && (
						<Typography variant="body2" color="textSecondary">
							Aucune organisation partenaire pour cette adresse.
						</Typography>
					)}
					{!loading && memberships.length > 0 && (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Organisation</TableCell>
									<TableCell>Rôle</TableCell>
									<TableCell>Statut</TableCell>
									<TableCell>Fonction</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{memberships.map((m) => (
									<TableRow key={m.id}>
										<TableCell>
											<Link to={`/partners/${m.partnerId}`}>{m.partnerName || m.partnerId}</Link>
										</TableCell>
										<TableCell>{m.role}</TableCell>
										<TableCell>{memberChip(m.userId)}</TableCell>
										<TableCell>{m.roleInOrg || '—'}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<Card style={{ marginTop: 16 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Partner Submission
					</Typography>
					{loading && <CircularProgress size={20} />}
					{!loading && !signup && (
						<Typography variant="body2" color="textSecondary">
							Aucune demande d'accès déposée avec cette adresse.
						</Typography>
					)}
					{!loading && signup && (
						<Table size="small">
							<TableBody>
								<TableRow>
									<TableCell variant="head">Société</TableCell>
									<TableCell>{signup.companyName || '—'}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell variant="head">Demandeur</TableCell>
									<TableCell>{`${signup.firstName} ${signup.lastName}`.trim() || '—'}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell variant="head">Téléphone</TableCell>
									<TableCell>{signup.phone || '—'}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell variant="head">Pays</TableCell>
									<TableCell>{signup.country || '—'}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell variant="head">Déposée le</TableCell>
									<TableCell>{signup.createdAt ? new Date(signup.createdAt).toLocaleString() : '—'}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell variant="head">Statut</TableCell>
									<TableCell>
										{signup.verified ? (
											<>
												<Chip size="small" color="warning" label="En attente d'arbitrage" />{' '}
												<Link to="/partner-signup-requests">Voir les demandes</Link>
											</>
										) : (
											/* ⚠️ Une demande non vérifiée n'apparaît PAS dans l'écran
											   d'arbitrage (`ListVerified`). C'est un reliquat de l'ancien
											   parcours, quand le code email devait être saisi : personne
											   ne la voit, et c'est l'explication la plus utile à donner
											   devant un compte qui semble bloqué. */
											<Chip size="small" color="default" label="Non vérifiée — invisible de l'arbitrage" />
										)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</>
	);
};
// #endregion
